import { useMemo } from 'react';
import { equals } from 'rambda';
import { type Stroke, StrokeType } from '@bynder-studio/misc';
import useTextSelection from 'packages/hooks/useTextSelection';

const unique = <T>(items: T[]) =>
    items.reduce<T[]>((acc, item) => (acc.some((existing) => equals(existing, item)) ? acc : [...acc, item]), []);

const omit = (stroke: Stroke, keys: (keyof Stroke)[]) => {
    const result = { ...stroke };

    keys.forEach((key) => delete result[key]);

    return result;
};

/**
 * The stroke of the selection. Different outline colors, weights or outline types do not make the
 * outline settings mixed: each of them is shown by its own control, which shows "Mixed" itself.
 * Having outline on some of the text only (or differing in the rest, e.g. punch out) is mixed.
 */
const useOutlineStroke = () => {
    const { stroke: strokes, strokeData } = useTextSelection();
    const [stroke, , mixedLabel] = strokeData;

    return useMemo(() => {
        const list = strokes as Stroke[];
        const isOutlined = (item: Stroke) => item.type !== StrokeType.NONE;
        const shared = list.map((item) => ({
            ...omit(item, ['strokeColor', 'width']),
            type: isOutlined(item) ? 'OUTLINE' : StrokeType.NONE,
        }));
        const isMixed = unique(shared).length > 1;
        const hasMixedColor = !isMixed && unique(list.map((item) => item.strokeColor ?? null)).length > 1;
        const hasMixedWidth = !isMixed && unique(list.map((item) => item.width)).length > 1;
        const hasMixedType = !isMixed && unique(list.map((item) => item.type)).length > 1;
        // Writing a stroke back with the first run's values would flatten the ones that differ.
        const mixedKeys = [hasMixedColor && 'strokeColor', hasMixedWidth && 'width', hasMixedType && 'type'].filter(
            Boolean,
        ) as (keyof Stroke)[];

        return {
            stroke,
            isMixed,
            hasMixedColor,
            hasMixedWidth,
            hasMixedType,
            mixedLabel,
            baseStroke: omit(stroke, mixedKeys),
        };
    }, [strokes, stroke, mixedLabel]);
};

export default useOutlineStroke;
